import { moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, Subject, catchError, concatMap, filter, map, of, switchMap, tap } from 'rxjs';
import { SelectionListKey } from '../../models/selection-list-default.model';
import { RequirementPriorityService } from '../../services/requirement-priority.service';
import {
  AuswahlListe,
  AuswahllistenEditorComponent,
  FeldFehler,
  WertAktion,
} from '../auswahllisten-editor/auswahllisten-editor.component';
import { MigrationDialogData, WertUmzugDialogComponent } from '../wert-umzug-dialog/wert-umzug-dialog.component';

const LISTE: SelectionListKey = 'requirement-priority';

interface Eintrag {
  nr: number;
  id: string | null;
  value: string;
  gespeichert: string;
}

@Component({
  selector: 'app-projekt-auswahllisten',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuswahllistenEditorComponent, TranslatePipe],
  template: `
    @if (zustand() === 'fehler') {
      <div class="pa-fehler" role="alert" data-testid="aw-ladefehler">
        <span>{{ 'auswahllisten.ladefehler' | translate }}</span>
        <button type="button" class="pa-fehler__erneut" data-testid="aw-erneut" (click)="laden()">
          {{ 'auswahllisten.erneut' | translate }}
        </button>
      </div>
    } @else {
      <app-auswahllisten-editor
        modus="projekt"
        [listen]="listen()"
        [laedt]="zustand() === 'laedt'"
        [feldFehler]="feldFehler()"
        (wertAktion)="ausfuehren($event)"
      />
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .pa-fehler {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
      padding: 1.25rem 1.5rem;
      border: 1px solid var(--to-border);
      border-radius: 0.75rem;
      background: var(--to-card);
      color: var(--to-red-fg);
      font-size: 0.875rem;
    }

    .pa-fehler__erneut {
      height: 2.125rem;
      padding: 0 0.75rem;
      border: 1px solid var(--to-border);
      border-radius: 0.5rem;
      background: var(--to-card);
      color: var(--to-text-body);
      font: inherit;
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--to-focus-ring);
        outline-offset: 2px;
      }
    }
  `,
})
export class ProjektAuswahllistenComponent implements OnInit {
  private readonly api = inject(RequirementPriorityService);
  private readonly dialog = inject(MatDialog);

  readonly projectId = input.required<string>();

  readonly zustand = signal<'laedt' | 'bereit' | 'fehler'>('laedt');
  private readonly eintraege = signal<Eintrag[]>([]);
  private readonly fehler = signal<{ nr: number | null; code: string } | null>(null);
  private readonly warteschlange = new Subject<() => Observable<unknown>>();
  private naechsteNr = 0;

  readonly feldFehler = computed<FeldFehler | null>(() => {
    const fehler = this.fehler();
    if (!fehler) return null;
    const index = fehler.nr === null ? null : this.eintraege().findIndex((e) => e.nr === fehler.nr);
    return index === -1 ? null : { listKey: LISTE, index, code: fehler.code };
  });

  readonly listen = computed<AuswahlListe[]>(() =>
    this.zustand() === 'laedt'
      ? []
      : [
          {
            listKey: LISTE,
            bereich: 'requirements',
            angepasst: false,
            werte: this.eintraege().map((e) => ({ value: e.value })),
            standard: [],
          },
        ],
  );

  constructor() {
    this.warteschlange
      .pipe(
        concatMap((auftrag) => auftrag()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.laden();
  }

  laden(): void {
    this.zustand.set('laedt');
    this.fehler.set(null);
    this.einreihen(() => this.holen());
  }

  ausfuehren(aktion: WertAktion): void {
    if (this.fehler()?.nr === null) this.fehler.set(null);
    switch (aktion.art) {
      case 'aendern':
        this.aendern(aktion.index, aktion.value);
        break;
      case 'festlegen':
        this.festlegen(aktion.index);
        break;
      case 'hinzufuegen':
        this.hinzufuegen(aktion.value);
        break;
      case 'entfernen':
        this.entfernen(aktion.index);
        break;
      case 'verschieben':
        this.verschieben(aktion.von, aktion.nach);
        break;
    }
  }

  private aendern(index: number, value: string): void {
    const nr = this.eintraege()[index]?.nr;
    this.eintraege.update((liste) => liste.map((e, i) => (i === index ? { ...e, value } : e)));
    this.fehlerLoeschen(nr);
  }

  private festlegen(index: number): void {
    const eintrag = this.eintraege()[index];
    const wert = eintrag?.value.trim();
    if (!eintrag || !wert || this.eintraege().some((e, i) => i !== index && gleich(e.value, wert))) return;
    this.setzen(eintrag.nr, { value: wert });
    if (wert !== eintrag.gespeichert) this.speichern(eintrag.nr, wert, eintrag.gespeichert);
  }

  private hinzufuegen(value: string): void {
    const nr = this.naechsteNr++;
    this.eintraege.update((liste) => [...liste, { nr, id: null, value, gespeichert: '' }]);
    this.speichern(nr, value, '');
  }

  private entfernen(index: number): void {
    const eintrag = this.eintraege()[index];
    if (!eintrag) return;
    if (eintrag.id === null) {
      this.wegnehmen(eintrag.nr);
      this.fehlerLoeschen(eintrag.nr);
      return;
    }
    this.einreihen(() => this.loeschen(eintrag.nr));
  }

  private loeschen(nr: number): Observable<unknown> {
    const eintrag = this.finden(nr);
    if (!eintrag?.id) return EMPTY;
    const id = eintrag.id;
    return this.api.countByPriority(this.projectId(), eintrag.gespeichert).pipe(
      catchError(() => this.listeFehlgeschlagen(false)),
      switchMap(({ count }) => (count === 0 ? of(null) : this.umzugszielWaehlen(eintrag, count))),
      switchMap((ziel) =>
        (ziel ? this.api.delete(this.projectId(), id, ziel) : this.api.delete(this.projectId(), id)).pipe(
          tap(() => this.wegnehmen(nr)),
          catchError(() => this.listeFehlgeschlagen(true)),
        ),
      ),
    );
  }

  private umzugszielWaehlen(eintrag: Eintrag, count: number): Observable<string> {
    const ziele = this.eintraege().filter((e): e is Eintrag & { id: string } => e.id !== null && e.nr !== eintrag.nr);
    return this.dialog
      .open<WertUmzugDialogComponent, MigrationDialogData, string | null>(WertUmzugDialogComponent, {
        data: {
          oldValue: eintrag.gespeichert,
          count,
          targets: ziele.map((e) => e.gespeichert),
          textSchluessel: 'requirements.priorities.migration',
        },
      })
      .afterClosed()
      .pipe(
        map((name) => ziele.find((e) => e.gespeichert === name)?.id),
        filter((id): id is string => !!id),
      );
  }

  private verschieben(von: number, nach: number): void {
    this.eintraege.update((liste) => {
      const neu = [...liste];
      moveItemInArray(neu, von, nach);
      return neu;
    });
    this.einreihen(() => {
      const ids = this.eintraege()
        .map((e) => e.id)
        .filter((id): id is string => id !== null);
      return this.api.reorder(this.projectId(), ids).pipe(catchError(() => this.listeFehlgeschlagen(true)));
    });
  }

  private speichern(nr: number, wert: string, vorher: string): void {
    this.setzen(nr, { gespeichert: wert });
    this.einreihen(() => {
      const eintrag = this.finden(nr);
      if (!eintrag) return EMPTY;
      const anfrage = eintrag.id
        ? this.api.rename(this.projectId(), eintrag.id, wert)
        : this.api.create(this.projectId(), wert);
      return anfrage.pipe(
        tap((antwort) => {
          const aktuell = this.finden(nr);
          this.fehlerLoeschen(nr);
          this.setzen(nr, {
            id: antwort.id,
            gespeichert: antwort.value,
            value: aktuell?.value === wert ? antwort.value : (aktuell?.value ?? antwort.value),
          });
        }),
        catchError((err: unknown) => {
          this.setzen(nr, { gespeichert: vorher });
          return this.wertFehlgeschlagen(nr, err);
        }),
      );
    });
  }

  private holen(): Observable<unknown> {
    return this.api.list(this.projectId()).pipe(
      tap((werte) => {
        this.eintraege.set(
          werte.map((w) => ({ nr: this.naechsteNr++, id: w.id, value: w.value, gespeichert: w.value })),
        );
        this.zustand.set('bereit');
      }),
      catchError(() => {
        this.zustand.set('fehler');
        return EMPTY;
      }),
    );
  }

  private wertFehlgeschlagen(nr: number, err: unknown): Observable<never> {
    const code = err instanceof HttpErrorResponse && [400, 409].includes(err.status) ? fehlerCode(err) : null;
    if (!code || !this.finden(nr))
      return this.listeFehlgeschlagen(err instanceof HttpErrorResponse && err.status === 404);
    this.fehler.set({ nr, code });
    return EMPTY;
  }

  private listeFehlgeschlagen(neuLaden: boolean): Observable<never> {
    this.fehler.set({ nr: null, code: 'LISTE' });
    if (neuLaden) this.einreihen(() => this.holen());
    return EMPTY;
  }

  private fehlerLoeschen(nr: number | undefined): void {
    if (nr !== undefined && this.fehler()?.nr === nr) this.fehler.set(null);
  }

  private einreihen(auftrag: () => Observable<unknown>): void {
    this.warteschlange.next(auftrag);
  }

  private finden(nr: number): Eintrag | undefined {
    return this.eintraege().find((e) => e.nr === nr);
  }

  private wegnehmen(nr: number): void {
    this.eintraege.update((liste) => liste.filter((e) => e.nr !== nr));
  }

  private setzen(nr: number, teil: Partial<Eintrag>): void {
    this.eintraege.update((liste) => liste.map((e) => (e.nr === nr ? { ...e, ...teil } : e)));
  }
}

function gleich(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function fehlerCode(err: HttpErrorResponse): string | null {
  const body = err.error as { code?: unknown } | null;
  return typeof body?.code === 'string' ? body.code : null;
}
